import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { ArrowLeftIcon, BookOpenIcon } from 'lucide-react'
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown'

import { Button } from '@repo/ui'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { glossaryTerm, relatedTerms, type GlossaryEntry } from 'docs/glossary'
import { articleForTerm } from 'help/articles'
import { helpArticlePath } from 'pages/Help/navState'
import { ROUTES } from 'routes'
import { font, radius, space } from 'settings/tokens'

export interface TermSheetProps {
  /** Glossary entry id the sheet opens on. */
  termId: string
  open: boolean
  onClose: () => void
}

/**
 * One glossary definition, shown WITHOUT leaving the page.
 *
 * The problem it solves is the one `apps/platform`'s `TermOverlay` solves, in the same
 * way and for the same reason: someone who wants one more sentence about "verified" has
 * a question about the thing in front of them, and navigating them into Help costs them
 * their place plus a back press. This is that component's behaviour on this app's
 * furniture — a bottom sheet rather than a dialog, because on the phone this app is
 * reached from (an NFC tap) a sheet is the control and a centred dialog is not.
 *
 * ## The definition is VERBATIM
 *
 * `short` and `long` render exactly as the artifact carries them. A term is defined once,
 * in `docs/glossary.json`, and every surface reads it from there (working-conventions
 * §44). The moment a call site paraphrases, the product has two answers to one question
 * and no mechanism compares them — and this is the worst place for that, because it is
 * read at the moment of confusion by someone with no way to tell it is stale.
 *
 * ## Following a related term SWAPS the body — it never opens a second sheet
 *
 * `seeAlso` is a graph, and walking a graph by opening a sheet per hop reaches three
 * levels almost immediately, which §52 rules out: the backdrops compound until the page
 * underneath is gone, and every level adds a dismissal with no way to tell which one
 * unwinds what. So there is ONE sheet and a `trail` — following a term pushes, and the
 * header grows a back arrow (`DrawerPopup`'s `headerLeft`, which exists for exactly this)
 * that pops it. One surface, one close control.
 *
 * Note this is only HALF the guarantee. Swapping in place stops a sheet stacking on
 * ITSELF; it does nothing about two different `(i)` affordances each rendering their own
 * `TermSheet`. That is what `TermSheetProvider` is for — read its note.
 *
 * ## Height
 *
 * Deliberately NOT pinned. §52 says not to resize a sheet between steps, but the failure
 * that rule records ("really jumpy and odd") is a sheet SNAPPING to a new height;
 * `DrawerPopup` puts `useAnimatedHeight` on its Paper, so the change is measured and
 * animated. Pinning a height instead would leave a one-line term sitting in a sheet sized
 * for the longest one, which is a visible cost paid on every open to avoid a transition
 * that is already smooth.
 */
export const TermSheet = ({ termId, open, onClose }: TermSheetProps) => {
  const theme = useTheme()
  const navigate = useNavigate()

  // Last element is what is on screen. A stack rather than a single id so the back arrow
  // walks the chain of terms the reader actually followed, not just back to the origin.
  const [trail, setTrail] = useState<string[]>([termId])

  // Reset on each OPEN, not on every `termId` change. Resetting on the prop alone would
  // be wrong while the sheet is open (nothing changes it mid-flight), and resetting only
  // on mount would be wrong too — the provider keeps ONE instance mounted for the app's
  // whole life, so the second open would resume the first reader's abandoned trail.
  useEffect(() => {
    if (open) setTrail([termId])
  }, [open, termId])

  const currentId = trail[trail.length - 1] ?? termId
  const entry = glossaryTerm(currentId)

  const goTo = useCallback((id: string) => setTrail(t => [...t, id]), [])
  const goBack = useCallback(() => setTrail(t => (t.length > 1 ? t.slice(0, -1) : t)), [])

  // Resolved once here rather than in the render. The filter inside `relatedTerms` is the
  // audience boundary, not tidiness — see its note in `docs/glossary`.
  const related = useMemo<GlossaryEntry[]>(() => (entry ? relatedTerms(entry) : []), [entry])

  // Unreachable in practice, at BOTH ends: `TermInfo` renders nothing unless its id
  // already resolved, and `related` is filtered to resolved entries before anything can
  // be pushed onto the trail. Kept because an open sheet with a header and no body is a
  // worse failure than no sheet, and `glossaryTerm` is typed as possibly-undefined anyway.
  if (!entry) return null

  /*
   * Where "Learn more" goes, computed in ONE place.
   *
   * The term's own best article when it has one, the Help index when it does not.
   * `tag-will-not-scan` and `lost-or-stolen` carry no `terms`, and two of the seven terms
   * (`provenance`, `transfer`) are covered by several articles — `articleForTerm` settles
   * both cases by `order`, so this component never has to.
   */
  const article = articleForTerm(entry.id)

  const goLearnMore = () => {
    onClose()
    navigate(article ? helpArticlePath(article.slug) : ROUTES.HELP)
  }

  /*
   * `key` on the swap so a content change re-runs the animation. Without it React reuses
   * the subtree, the new term appears with no transition at all, and the step reads as a
   * glitch rather than as going deeper.
   *
   * Horizontal, matching the app's own page-push vocabulary — going INTO a related term
   * is the same gesture as pushing a page, so it should move the same way.
   */
  const body = (
    <Box
      key={currentId}
      sx={{
        '@keyframes veraTermSwapIn': {
          from: { opacity: 0, transform: 'translateX(6px)' },
          to: { opacity: 1, transform: 'translateX(0)' },
        },
        animation: 'veraTermSwapIn 180ms cubic-bezier(0.32, 0.72, 0, 1)',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* 16px between blocks. `apps/app` is on MUI's default 8px base, NOT @repo/ui's
          4px — copying a platform `spacing={4}` here would render 32px (§29). */}
      <Stack spacing={2}>
        {/* Verbatim from the artifact. Never retyped, never paraphrased (§44). */}
        <Typography sx={{ fontSize: font.body, lineHeight: 1.6 }}>{entry.short}</Typography>

        {/*
          `long` renders as MARKDOWN so a definition can reference another term inline —
          `[the words](term:other-id)` — instead of only via the RELATED chips underneath.
          §44's authored form, and the same corpus syntax the Help articles already use.

          Only `long`. `short` stays plain text on purpose: §44 makes it the tooltip body,
          and it is consumed raw elsewhere (`pages/Help/index.tsx:358` renders it as a
          search sublabel). Markdown in `short` would render as source in those places —
          one string appearing two different ways, which is the drift the single-source
          rule exists to stop.

          A term link here calls `goTo`, NOT `Term`/`openTerm`. `Term` opens a sheet, and
          this IS the sheet: nesting one inside itself is the three-deep stack §52 forbids,
          and the component's own docblock says it must never stack. `goTo` pushes onto the
          trail and swaps the body in place, exactly what the RELATED chips below do — so
          both routes to a related term behave identically.
        */}
        {entry.long && (
          <Typography
            component='div'
            sx={{
              fontSize: font.body,
              lineHeight: 1.6,
              color: theme.palette.text.secondary,
              '& p': { margin: 0 },
              '& p + p': { marginTop: `${space.lg}px` },
            }}
          >
            <ReactMarkdown
              /*
                react-markdown's default allow-list is http|https|ircs|mailto|xmpp and it
                rewrites every other scheme to the EMPTY STRING — so `term:` would arrive
                with no href, the branch below would never fire, and the link would render
                as dead text with no error. §44 flags this exact trap; `ArticlePage.tsx:79`
                carries the same widening for the same reason.
              */
              urlTransform={url => (url.startsWith('term:') ? url : defaultUrlTransform(url))}
              components={{
                a: ({ children, href }) =>
                  href?.startsWith('term:') ? (
                    <Box
                      component='span'
                      role='button'
                      tabIndex={0}
                      onClick={() => goTo(href.slice('term:'.length))}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          goTo(href.slice('term:'.length))
                        }
                      }}
                      sx={{
                        // Matches `Term.tsx` exactly — a term is underlined the same way
                        // whether it sits in an article or inside another definition.
                        cursor: 'pointer',
                        color: theme.palette.text.primary,
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '3px',
                      }}
                    >
                      {children}
                    </Box>
                  ) : (
                    // Not a term: render the words, drop the link. A glossary definition
                    // has no business navigating out of the sheet, and a dead <a> that
                    // looks tappable is worse than plain text.
                    <>{children}</>
                  ),
              }}
            >
              {entry.long}
            </ReactMarkdown>
          </Typography>
        )}

        {related.length > 0 && (
          <Stack spacing={1}>
            <Typography
              variant='body2'
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Related
            </Typography>
            {/* 12px between chips, stated in px for the reason on `space`. */}
            <Stack direction='row' flexWrap='wrap' sx={{ gap: `${space.md}px` }}>
              {related.map(r => (
                // A BUTTON, not a link. Activating it swaps this sheet's body; it does
                // not navigate. Styling it as a link would promise a destination and a
                // long-pressable URL that do not exist.
                //
                // RECTANGULAR (radius.sm), not a pill. In this product a pill is the
                // shape of a STATUS — active, pending, transferred — so a pill here
                // reads as a label describing the term rather than a control that does
                // something to it. The rounded rectangle is the shape of a control, and
                // it keeps the tap target that a bare underline would give up on a
                // phone this app is reached from by tapping a tag.
                <Typography
                  key={r.id}
                  component='button'
                  type='button'
                  onClick={() => goTo(r.id)}
                  sx={{
                    // NOT `font: 'inherit'` — that is a shorthand, so it resets font-size
                    // and silently beats a `fontSize` beside it. Reset only what a
                    // <button> actually needs resetting.
                    fontFamily: 'inherit',
                    fontWeight: 'inherit',
                    fontSize: font.label,
                    lineHeight: 1.4,
                    background: 'none',
                    // `!important` because `#vera-shadow-root *` sets border-color at ID
                    // specificity and silently discards an sx border colour without it
                    // (see settings/tokens).
                    border: `1px solid ${theme.palette.grey[400]} !important`,
                    borderRadius: `${radius.sm}px`,
                    px: '12px',
                    py: '6px',
                    color: theme.palette.text.primary,
                    cursor: 'pointer',
                    textAlign: 'left',
                    '@media (hover: hover)': {
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.palette.text.primary}`,
                      outlineOffset: '2px',
                    },
                  }}
                >
                  {r.term}
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}

        {/*
          The escape hatch, deliberately quiet. The reader opened this for one definition
          and is usually done; a loud CTA would rebuild the navigation this sheet exists
          to avoid. Anchored on the term CURRENTLY shown, not the one that opened the
          sheet — after two hops, "learn more" means "about where I am now".

          Wrapped so the Button shrinks to its label instead of stretching to the Stack's
          full width, which would read as a primary action.
        */}
        <Box>
          <Button
            variant='text'
            size='small'
            onClick={goLearnMore}
            startIcon={<BookOpenIcon size={16} strokeWidth={1.5} />}
            sx={{ px: 0, color: theme.palette.text.secondary }}
          >
            {article ? 'Learn more' : 'Browse help'}
          </Button>
        </Box>
      </Stack>
    </Box>
  )

  return (
    <DrawerPopup
      title={entry.term}
      handleOpen={open}
      handleClose={onClose}
      headerLeft={
        trail.length > 1 ? (
          <IconButton
            onClick={goBack}
            aria-label='Back to the previous term'
            size='small'
            sx={{
              // Pulled back so the arrow's BOX, not its glyph, starts at the header's
              // padding edge — the same alignment `PageHeader`'s back control takes.
              ml: '-8px',
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.text.primary },
            }}
          >
            <ArrowLeftIcon size={20} strokeWidth={1.5} />
          </IconButton>
        ) : undefined
      }
      child={body}
    />
  )
}
